import type { AuthPromptMoment, AuthPromptMomentConfig } from './authPrompt.types';

const baseBenefits = {
  account: [
    'Orders and tracking in one dashboard',
    'Saved designs and addresses',
    'Seller and creator tools when you are ready',
  ],
};

export const AUTH_PROMPT_MOMENTS: Record<AuthPromptMoment, AuthPromptMomentConfig> = {
  checkout: {
    eyebrow: 'Checkout',
    title: 'Continue faster with your account',
    description: 'Sign in to autofill your details — or continue as guest. Your cart stays right here.',
    googleLabel: 'Continue with Google',
    emailCta: 'Sign in with email',
    benefits: [
      'Saved addresses and faster checkout',
      'Live order tracking in your account',
      'Your designs stay linked to your profile',
    ],
    trustLine: 'Guest checkout always available',
    googleIntent: 'login',
    analyticsSurface: 'checkout_modal',
    allowGuest: true,
    guestContinueLabel: 'Continue as guest',
    showRegisterLink: false,
  },
  save_design: {
    eyebrow: 'Save your work',
    title: 'Save your design to your account',
    description: 'Create a free account in seconds — your canvas stays exactly as you left it.',
    googleLabel: 'Save with Google',
    emailCta: 'Continue with email',
    benefits: [
      'Designs saved across devices',
      'Edit and reorder anytime',
      'Publish when you are ready',
    ],
    trustLine: 'Free forever · No credit card',
    googleIntent: 'register',
    analyticsSurface: 'editor_modal',
    allowGuest: false,
    guestContinueLabel: 'Cancel',
    showRegisterLink: true,
  },
  export_design: {
    eyebrow: 'Export',
    title: 'Export your design',
    description: 'Sign in to download and keep a permanent copy of your design.',
    googleLabel: 'Continue with Google',
    emailCta: 'Continue with email',
    benefits: [
      'High-quality exports saved to your account',
      'Re-export anytime from your library',
      'Order prints directly from saved designs',
    ],
    trustLine: 'Your canvas is preserved during sign-in',
    googleIntent: 'register',
    analyticsSurface: 'editor_modal',
    allowGuest: false,
    guestContinueLabel: 'Cancel',
    showRegisterLink: true,
  },
  wishlist: {
    eyebrow: 'Save for later',
    title: 'Keep this piece on your wishlist',
    description: 'Sign in or create a free account to save favorites and shop them later.',
    googleLabel: 'Continue with Google',
    emailCta: 'Continue with email',
    benefits: [
      'Wishlist synced across devices',
      'Faster checkout when you are ready',
      'Order history in one place',
    ],
    trustLine: 'Free account · Your data is never sold',
    googleIntent: 'register',
    analyticsSurface: 'auth_prompt',
    allowGuest: false,
    guestContinueLabel: 'Cancel',
    showRegisterLink: true,
  },
  seller_program: {
    eyebrow: 'Seller Program',
    title: 'Start selling this design',
    description: 'Create your seller account to publish products and track earnings.',
    googleLabel: 'Continue with Google',
    emailCta: 'Continue with email',
    benefits: [
      'One login for shop and seller tools',
      'Track applications and onboarding',
      'Get paid when orders ship',
    ],
    trustLine: 'Applications reviewed for quality',
    googleIntent: 'register',
    analyticsSurface: 'seller_program',
    allowGuest: false,
    guestContinueLabel: 'Cancel',
    showRegisterLink: true,
  },
  creator_program: {
    eyebrow: 'Creator Studio',
    title: 'Join the creator program',
    description: 'Earn commission on referrals — one account for your storefront and creator dashboard.',
    googleLabel: 'Continue with Google',
    emailCta: 'Continue with email',
    benefits: [
      'Custom referral links and analytics',
      'Campaign alerts and payout tracking',
      'Public creator profile at /r/you',
    ],
    trustLine: 'Free to join · Transparent payouts',
    googleIntent: 'register',
    analyticsSurface: 'creator_program',
    allowGuest: false,
    guestContinueLabel: 'Cancel',
    showRegisterLink: true,
  },
  account_access: {
    eyebrow: 'Your account',
    title: 'Sign in to continue',
    description: 'Access orders, saved designs, wishlist, and seller or creator tools in one place.',
    googleLabel: 'Continue with Google',
    emailCta: 'Sign in with email',
    benefits: baseBenefits.account,
    trustLine: 'Secure sign-in · We never sell your data',
    googleIntent: 'register',
    analyticsSurface: 'auth_prompt',
    allowGuest: false,
    guestContinueLabel: 'Cancel',
    showRegisterLink: true,
  },
  track_order: {
    eyebrow: 'Order tracking',
    title: 'Track your order',
    description: 'Login to see delivery status, invoices, and support options.',
    googleLabel: 'Continue with Google',
    emailCta: 'Sign in with email',
    benefits: [
      'Real-time delivery updates',
      'Invoices and order history',
      'Faster support for your orders',
    ],
    trustLine: 'Secure access to your orders only',
    googleIntent: 'login',
    analyticsSurface: 'auth_prompt',
    allowGuest: false,
    guestContinueLabel: 'Cancel',
    showRegisterLink: false,
  },
  billing_access: {
    eyebrow: 'Billing',
    title: 'Secure billing access',
    description: 'Login to view invoices, payments, and billing history.',
    googleLabel: 'Continue with Google',
    emailCta: 'Sign in with email',
    benefits: [
      'Download invoices anytime',
      'Payment history in one place',
      'Manage billing details securely',
    ],
    trustLine: 'Encrypted · Never shared with third parties',
    googleIntent: 'login',
    analyticsSurface: 'auth_prompt',
    allowGuest: false,
    guestContinueLabel: 'Cancel',
    showRegisterLink: false,
  },
  publish_design: {
    eyebrow: 'Start selling',
    title: 'Start selling this design',
    description: 'Create your seller account to publish products and track earnings.',
    googleLabel: 'Continue with Google',
    emailCta: 'Continue with email',
    benefits: [
      'Publish to your storefront',
      'Track orders and earnings',
      'Print-on-demand fulfillment handled for you',
    ],
    trustLine: 'No inventory required',
    googleIntent: 'register',
    analyticsSurface: 'seller_program',
    allowGuest: false,
    guestContinueLabel: 'Cancel',
    showRegisterLink: true,
  },
  refund_request: {
    eyebrow: 'Returns',
    title: 'Request a refund or return',
    description: 'Login to manage your order and follow the request status.',
    googleLabel: 'Continue with Google',
    emailCta: 'Sign in with email',
    benefits: [
      'Submit returns in a few clicks',
      'Track refund status live',
      'Message support on your order',
    ],
    trustLine: 'We respond within 1–2 business days',
    googleIntent: 'login',
    analyticsSurface: 'auth_prompt',
    allowGuest: false,
    guestContinueLabel: 'Cancel',
    showRegisterLink: false,
  },
  review_product: {
    eyebrow: 'Reviews',
    title: 'Share your review',
    description: 'Login to review products you purchased and help other shoppers.',
    googleLabel: 'Continue with Google',
    emailCta: 'Sign in with email',
    benefits: [
      'Only verified buyers can review',
      'Help the Qumashi community',
      'Your orders linked automatically',
    ],
    trustLine: 'Honest reviews build trust',
    googleIntent: 'login',
    analyticsSurface: 'auth_prompt',
    allowGuest: false,
    guestContinueLabel: 'Cancel',
    showRegisterLink: false,
  },
  follow_store: {
    eyebrow: 'Follow',
    title: 'Follow this store',
    description: 'Login to save this store and get updates about new products.',
    googleLabel: 'Continue with Google',
    emailCta: 'Continue with email',
    benefits: [
      'New drop alerts from this store',
      'Saved stores in your account',
      'Faster checkout from favorites',
    ],
    trustLine: 'Unsubscribe anytime',
    googleIntent: 'register',
    analyticsSurface: 'auth_prompt',
    allowGuest: false,
    guestContinueLabel: 'Cancel',
    showRegisterLink: true,
  },
  save_cart: {
    eyebrow: 'Your cart',
    title: 'Save your cart',
    description: 'Login to keep your cart across devices and continue later.',
    googleLabel: 'Continue with Google',
    emailCta: 'Continue with email',
    benefits: [
      'Cart synced on phone and desktop',
      'Pick up checkout where you left off',
      'Order history when you buy',
    ],
    trustLine: 'Optional — you can still checkout as guest',
    googleIntent: 'register',
    analyticsSurface: 'auth_prompt',
    allowGuest: true,
    guestContinueLabel: 'Continue without saving',
    showRegisterLink: true,
  },
  creator_storefront_checkout: {
    eyebrow: 'Checkout',
    title: 'Continue your order',
    description: 'Login or continue as guest. Your creator offer stays connected to this order.',
    googleLabel: 'Continue with Google',
    emailCta: 'Sign in with email',
    benefits: [
      'Creator attribution preserved on your order',
      'Track delivery in your account',
      'Reorder favorite designs quickly',
    ],
    trustLine: 'Guest checkout available',
    googleIntent: 'login',
    analyticsSurface: 'checkout_modal',
    allowGuest: true,
    guestContinueLabel: 'Continue as guest',
    showRegisterLink: false,
  },
};

export function getAuthPromptConfig(
  moment: AuthPromptMoment,
  preferSignIn?: boolean,
): AuthPromptMomentConfig {
  const base = AUTH_PROMPT_MOMENTS[moment];
  if (!preferSignIn || moment !== 'checkout') return base;
  return {
    ...base,
    title: 'Continue faster with your account',
    description:
      'Sign in to autofill your details — or continue as guest. Your cart stays right here.',
    googleIntent: 'login',
  };
}
